import { Component, NgZone } from '@angular/core'
import { APIService } from 'api/module'
import { NotifyService } from 'common/util/services/notify.service'
import { ParametersService } from 'main/services/parameters.service'
import { IdentityService } from 'main/services/identity.service'
import { PanelService } from 'panel/services/panel.service'
import { RouteLoggerService } from 'common/util/services/routeLogger.service'
import { ProxyCacheService } from 'panel/services/proxyCache.service'
import { routeChangingTrigger } from 'root/components/appRoot.component'

@Component({
  selector: 'panel-root',
  templateUrl: './panelRoot.component.pug',
  styleUrls: ['./panelRoot.component.scss'],
  animations: [routeChangingTrigger],
})
export class PanelRootComponent {
  proxyMode = false
  pathProxyMap = {}
  proxyPanelVisible = false

  constructor (
    private api: APIService,
    private notify: NotifyService,
    public identity: IdentityService,
    public panel: PanelService,
    public proxyCache: ProxyCacheService,
    private zone: NgZone,
    public parameters: ParametersService,
    public routeLogger: RouteLoggerService,
  ) {
  }

  async ngOnInit () {
    await this.panel.ready
    if (['PPRO', 'Resolve'].includes(this.panel.app)) {
      setInterval(async () => {
        const proxyMode = await this.panel.plugin.isProxyMode()
        if (proxyMode !== this.proxyMode) {
          this.zone.run(() => {
            this.proxyMode = proxyMode
          })
        }
      }, 1000)
    }

    if (this.panel.native) {
      const proxyPath = this.panel.plugin.getConfig('proxyPath')
      try {
        this.checkProxyPath(proxyPath)
      } catch (error) {
        return
      }

      this.panel.plugin.setConfig('proxyPath', proxyPath)
      this.proxyCache.cachePath = proxyPath
    }
  }

  restart () {
    this.panel.restartExtension()
  }

  async cacheAndAttachSequenceProxies () {
    const paths = await this.panel.plugin.getActiveSequencePaths()
    await this.cacheProxies(paths)
    this.attachProxies(paths)
  }

  async cacheAndAttachProjectProxies () {
    const paths = await this.panel.plugin.getProjectPaths()
    await this.cacheProxies(paths)
    this.attachProxies(paths)
  }

  async cacheProxies (paths) {
    const root = this.panel.plugin.currentMediaRootID
    let foundAssets = await this.api.private.locateProxies({
      locateProxiesEndpointRequest: {
        paths,
      },
      forRoot: root,
      includeProxies: true,
    })
    foundAssets = foundAssets.filter(x => x.asset.type === 'video')
    await Promise.all(foundAssets.map(x => this.proxyCache.scheduleCacheAsset(x.asset, x.asset.defaultProxy?.id)))
  }

  async updateProxyPathMap (paths) {
    const root = this.panel.plugin.currentMediaRootID
    const foundAssets = await this.api.private.locateProxies({
      locateProxiesEndpointRequest: {
        paths,
      },
      forRoot: root,
      includeProxies: true,
    })
    for (const foundAsset of foundAssets) {
      if (await this.proxyCache.isCached(foundAsset.asset, foundAsset.asset.defaultProxy?.id)) {
        this.pathProxyMap[foundAsset.path] = this.proxyCache.getProxyPath(foundAsset.syncId, foundAsset.asset.defaultProxy?.id)
      }
    }
  }

  async attachProxies (paths) {
    await this.updateProxyPathMap(paths)
    this.setProxyMode(true)
    this.panel.plugin.attachProxies(this.pathProxyMap)
  }

  async proxyDirChange () {
    if (this.proxyCache.runningJobs.length) {
      return
    }
    const result = await this.panel.plugin.getLocalPath({
      defaultPath: this.panel.plugin.getConfig('proxyPath'),
      properties: ['openDirectory'],
    })

    if (result && !this.proxyCache.runningJobs.length) {
      try {
        this.checkProxyPath(result)
      } catch (error) {
        this.notify.error(error)
        return
      }

      this.panel.plugin.setConfig('proxyPath', result)
      this.proxyCache.cachePath = result
    }
  }

  checkProxyPath (proxyPath) {
    const path = window['nodeRequire']('path')
    if (!proxyPath) {
      return false
    }

    // eslint-disable-next-line @typescript-eslint/init-declarations
    let stats

    if (this.panel.app === 'PPRO') {
      stats = this.panel.fs.stat(proxyPath).data
    } else {
      stats = this.panel.fs.statSync(proxyPath)
    }

    if (!stats.isDirectory()) {
      throw new Error('Proxy path is not a directory')
    }

    const testpath = path.join(proxyPath, '.testwrite.txt')
    try {
      if (this.panel.app === 'PPRO') {
        this.panel.fs.writeFile(testpath, 'test')
        this.panel.fs.deleteFile(testpath)
      } else {
        this.panel.fs.writeFileSync(testpath, 'test')
        this.panel.fs.unlinkSync(testpath)
      }
    } catch (err) {
      throw new Error('Unable to write file in proxy path')
    }

    return true
  }

  setProxyMode (enabled) {
    this.proxyMode = enabled
    this.panel.plugin.setProxyMode(enabled)
  }
}
